/**
 * Browser-native media transport plugin. The workspace talks to this small
 * contract instead of RTCPeerConnection directly, so an SFU adapter can later
 * replace it without changing messaging screens.
 */
export class NativeWebRTCTransport {
  constructor({ iceServers, onIceCandidate, onTrack, onConnectionStateChange }) {
    this.iceServers = iceServers;
    this.onIceCandidate = onIceCandidate;
    this.onTrack = onTrack;
    this.onConnectionStateChange = onConnectionStateChange;
    this.peer = null;
    this.localStream = null;
    this.pendingCandidates = [];
    this.facingMode = 'user';
  }

  async initialize(mediaType) {
    if (!navigator.mediaDevices?.getUserMedia || !window.RTCPeerConnection) {
      throw new Error('Calling is not supported in this browser.');
    }
    this.localStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: mediaType === 'video',
    });
    this.peer = new window.RTCPeerConnection({ iceServers: this.iceServers });
    this.localStream.getTracks().forEach((track) => this.peer.addTrack(track, this.localStream));
    this.peer.onicecandidate = ({ candidate }) => candidate && this.onIceCandidate?.(candidate.toJSON?.() || candidate);
    this.peer.ontrack = ({ streams }) => this.onTrack?.(streams[0]);
    this.peer.onconnectionstatechange = () => this.onConnectionStateChange?.(this.peer.connectionState);
    return this.localStream;
  }

  async createOffer() {
    const offer = await this.peer.createOffer();
    await this.peer.setLocalDescription(offer);
    return { type: offer.type, sdp: offer.sdp };
  }

  async applyOfferAndCreateAnswer(description) {
    await this.peer.setRemoteDescription(description);
    await this.flushPendingCandidates();
    const answer = await this.peer.createAnswer();
    await this.peer.setLocalDescription(answer);
    return { type: answer.type, sdp: answer.sdp };
  }

  async applyAnswer(description) {
    await this.peer.setRemoteDescription(description);
    await this.flushPendingCandidates();
  }

  async addIceCandidate(candidate) {
    if (!candidate || !this.peer) return;
    if (!this.peer.remoteDescription) {
      this.pendingCandidates.push(candidate);
      return;
    }
    await this.peer.addIceCandidate(candidate);
  }

  async flushPendingCandidates() {
    if (!this.peer?.remoteDescription) return;
    const pending = this.pendingCandidates.splice(0);
    for (const candidate of pending) await this.peer.addIceCandidate(candidate);
  }

  toggleMute() {
    const tracks = this.localStream?.getAudioTracks() || [];
    const shouldDisable = tracks.some((track) => track.enabled);
    tracks.forEach((track) => { track.enabled = !shouldDisable; });
    return shouldDisable;
  }

  toggleCamera() {
    const tracks = this.localStream?.getVideoTracks() || [];
    const shouldDisable = tracks.some((track) => track.enabled);
    tracks.forEach((track) => { track.enabled = !shouldDisable; });
    return shouldDisable;
  }

  async switchCamera() {
    const oldTrack = this.localStream?.getVideoTracks?.()[0];
    const sender = this.peer?.getSenders?.().find((item) => item.track?.kind === 'video');
    if (!oldTrack || !sender?.replaceTrack) throw new Error('Camera switching is not supported on this device.');
    const nextFacingMode = this.facingMode === 'user' ? 'environment' : 'user';
    const replacementStream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: nextFacingMode } },
      audio: false,
    });
    const replacement = replacementStream.getVideoTracks()[0];
    if (!replacement) throw new Error('No alternate camera is available.');
    await sender.replaceTrack(replacement);
    oldTrack.stop();
    if (this.localStream.removeTrack && this.localStream.addTrack) {
      this.localStream.removeTrack(oldTrack);
      this.localStream.addTrack(replacement);
    }
    this.facingMode = nextFacingMode;
    return nextFacingMode;
  }

  stop() {
    this.peer?.close();
    this.peer = null;
    this.localStream?.getTracks().forEach((track) => track.stop());
    this.localStream = null;
    this.pendingCandidates = [];
  }
}

export default NativeWebRTCTransport;
